// pages/Personal Attendance/TeamWorkHours.js
//
// Owner 2026-10-15: "we need something so that their manager can adjust time. Say the
// manager adjusts time from 7:30 am to 4 pm, then it should calculate for that
// particular employee from 7:30 am to 4; all other counting rules would be the same."
//
// A manager (anyone above the person in the supervisor chain), HR and IT Admin set a
// person's OWN daily work hours here. Without a window the company hours apply
// (8:00 to 4:30). The payroll card, the late-in / early-out reports and the absence
// rule read the window on the days it covers. Salaried pay is a day rate and does not
// change; hourly pay counts within the person's own window.
//
// Backend: GET  /api/attendance/work-window/team
//          GET  /api/attendance/work-window/{empId}
//          POST /api/attendance/work-window
//          DELETE /api/attendance/work-window/{id}?lastDay=yyyy-MM-dd
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Badge, Button, Card, CardBody, Col, Container, Form, Input, Label,
    Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table,
} from "reactstrap";
import { toast } from "react-toastify";
import api from "../../api";
import useBackCloses from "../../hooks/useBackCloses";
import { DashboardPageHeader, EmptyState } from "../../Components/Common/DashboardKit";

const pick = (o, ...keys) => { for (const k of keys) { if (o && o[k] !== undefined && o[k] !== null) return o[k]; } return undefined; };
const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtTime = (hhmm) => {
    if (!hhmm) return "";
    const [h, m] = String(hhmm).split(":").map(Number);
    if (Number.isNaN(h)) return hhmm;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
};
const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const errMsg = (e, fallback) => e?.response?.data?.Message || e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;

const WindowBadge = ({ w, companyStart, companyEnd }) => {
    if (!w) {
        return <span className="text-muted">{fmtTime(companyStart)} – {fmtTime(companyEnd)} <small>(company hours)</small></span>;
    }
    return (
        <span>
            <strong>{fmtTime(pick(w, "startTime", "StartTime"))} – {fmtTime(pick(w, "endTime", "EndTime"))}</strong>
            <small className="text-muted ms-2">
                from {fmtDate(pick(w, "effectiveFrom", "EffectiveFrom"))}
                {pick(w, "effectiveTo", "EffectiveTo") ? ` to ${fmtDate(pick(w, "effectiveTo", "EffectiveTo"))}` : ""}
            </small>
        </span>
    );
};

const TeamWorkHours = () => {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [company, setCompany] = useState({ start: "08:00", end: "16:30" });
    const [search, setSearch] = useState("");

    // editor
    const [editRow, setEditRow] = useState(null);
    const [form, setForm] = useState({ startTime: "07:30", endTime: "16:00", effectiveFrom: todayIso(), effectiveTo: "", note: "" });
    const [history, setHistory] = useState([]);
    const [saving, setSaving] = useState(false);
    const [ending, setEnding] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("attendance/work-window/team");
            setRows(pick(data, "rows", "Rows") || []);
            setCompany({ start: pick(data, "companyStart", "CompanyStart") || "08:00", end: pick(data, "companyEnd", "CompanyEnd") || "16:30" });
        } catch (e) {
            toast.error(errMsg(e, "The team list could not be loaded."));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openEditor = async (row) => {
        const cur = pick(row, "current", "Current");
        setForm({
            startTime: cur ? pick(cur, "startTime", "StartTime") : company.start,
            endTime: cur ? pick(cur, "endTime", "EndTime") : company.end,
            effectiveFrom: todayIso(),
            effectiveTo: "",
            note: "",
        });
        setEditRow(row);
        setHistory([]);
        try {
            const { data } = await api.get(`attendance/work-window/${encodeURIComponent(pick(row, "empId", "EmpId"))}`);
            setHistory(pick(data, "rows", "Rows") || []);
        } catch { /* history is optional */ }
    };
    const closeEditor = () => { if (!saving) setEditRow(null); };
    useBackCloses(!!editRow, closeEditor);

    const save = async (e) => {
        e?.preventDefault?.();
        if (!editRow) return;
        setSaving(true);
        try {
            await api.post("attendance/work-window", {
                empId: pick(editRow, "empId", "EmpId"),
                startTime: form.startTime,
                endTime: form.endTime,
                effectiveFrom: form.effectiveFrom,
                effectiveTo: form.effectiveTo || null,
                note: form.note || null,
            });
            toast.success(`${pick(editRow, "name", "Name")}: ${fmtTime(form.startTime)} to ${fmtTime(form.endTime)} from ${fmtDate(form.effectiveFrom)}.`);
            setEditRow(null);
            load();
        } catch (err) {
            toast.error(errMsg(err, "The hours could not be saved."));
        } finally {
            setSaving(false);
        }
    };

    const endWindow = async (row, w) => {
        const id = pick(w, "id", "Id");
        const name = pick(row, "name", "Name");
        if (!window.confirm(`${name}: back to the company hours ${fmtTime(company.start)} to ${fmtTime(company.end)} from today?`)) return;
        setEnding(id);
        try {
            await api.delete(`attendance/work-window/${id}`);
            toast.success(`${name} is back on the company hours.`);
            if (editRow) setEditRow(null);
            load();
        } catch (err) {
            toast.error(errMsg(err, "The window could not be ended."));
        } finally {
            setEnding(null);
        }
    };

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) => {
            if (!q) return true;
            return [pick(r, "name", "Name"), pick(r, "empId", "EmpId"), pick(r, "dept", "Dept")].some((v) => String(v || "").toLowerCase().includes(q));
        });
    }, [rows, search]);

    const span = (() => {
        const [sh, sm] = String(form.startTime || "").split(":").map(Number);
        const [eh, em] = String(form.endTime || "").split(":").map(Number);
        if ([sh, sm, eh, em].some(Number.isNaN)) return null;
        const h = (eh * 60 + em - (sh * 60 + sm)) / 60;
        return h > 0 ? h : null;
    })();

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-time-line"
                    eyebrow="Attendance · My Team"
                    title="Team Work Hours"
                    subtitle={`Hourly employees only. Without their own hours, the company hours ${fmtTime(company.start)} to ${fmtTime(company.end)} apply.`}
                    tone="blue"
                    breadcrumb={[{ label: "Attendance", to: "/workflows?wf=pac" }, { label: "Team Work Hours" }]}
                />

                <Card>
                    <CardBody>
                        <Row className="g-2 align-items-center mb-3">
                            <Col md={5}>
                                <Input placeholder="Search name, ID or department" value={search} onChange={(e) => setSearch(e.target.value)} />
                            </Col>
                            <Col className="text-md-end text-muted small">
                                Hourly pay counts inside the person's hours (lunch rule unchanged). Late-in, early-out and the expected clock-out use them too.
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5"><Spinner size="sm" /> Loading…</div>
                        ) : visible.length === 0 ? (
                            <EmptyState title="Nobody to show" message={rows.length === 0 ? "No hourly employee reports to you." : "No one matches the search."} />
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Employee</th>
                                            <th>Dept</th>
                                            <th>Pay</th>
                                            <th>Work hours now</th>
                                            <th>Coming up</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visible.map((r) => {
                                            const cur = pick(r, "current", "Current");
                                            const up = pick(r, "upcoming", "Upcoming");
                                            const cls = String(pick(r, "payClass", "PayClass") || "");
                                            return (
                                                <tr key={pick(r, "empId", "EmpId")}>
                                                    <td>
                                                        <div className="fw-semibold">{pick(r, "name", "Name")}</div>
                                                        <small className="text-muted">{pick(r, "empId", "EmpId")}</small>
                                                    </td>
                                                    <td>{pick(r, "dept", "Dept") || "—"}</td>
                                                    <td>
                                                        <Badge color={cls === "Hourly" ? "info" : cls === "Salaried" ? "secondary" : "warning"} pill>{cls || "?"}</Badge>
                                                    </td>
                                                    <td><WindowBadge w={cur} companyStart={company.start} companyEnd={company.end} /></td>
                                                    <td>{up ? <WindowBadge w={up} /> : <span className="text-muted">—</span>}</td>
                                                    <td className="text-end text-nowrap">
                                                        <Button size="sm" color="primary" outline className="me-1" onClick={() => openEditor(r)}>
                                                            {cur ? "Change" : "Set hours"}
                                                        </Button>
                                                        {cur && (
                                                            <Button size="sm" color="secondary" outline disabled={ending === pick(cur, "id", "Id")} onClick={() => endWindow(r, cur)}>
                                                                {ending === pick(cur, "id", "Id") ? "…" : "Company hours"}
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                <Modal isOpen={!!editRow} toggle={closeEditor} centered size="lg">
                    <Form onSubmit={save}>
                        <ModalHeader toggle={closeEditor}>
                            {editRow ? `${pick(editRow, "name", "Name")} · work hours` : ""}
                        </ModalHeader>
                        <ModalBody>
                            <Row className="g-3">
                                <Col md={3}>
                                    <Label for="ww-start">Start</Label>
                                    <Input id="ww-start" type="time" step="300" value={form.startTime} required onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                                </Col>
                                <Col md={3}>
                                    <Label for="ww-end">End</Label>
                                    <Input id="ww-end" type="time" step="300" value={form.endTime} required onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
                                </Col>
                                <Col md={3}>
                                    <Label for="ww-from">From</Label>
                                    <Input id="ww-from" type="date" value={form.effectiveFrom} required onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} />
                                </Col>
                                <Col md={3}>
                                    <Label for="ww-to">Until <small className="text-muted">(optional)</small></Label>
                                    <Input id="ww-to" type="date" value={form.effectiveTo} min={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })} />
                                </Col>
                                <Col md={12}>
                                    <Label for="ww-note">Note <small className="text-muted">(optional)</small></Label>
                                    <Input id="ww-note" maxLength={200} value={form.note} placeholder="Why these hours, for the record" onChange={(e) => setForm({ ...form, note: e.target.value })} />
                                </Col>
                            </Row>
                            <div className="text-muted small mt-3">
                                {span ? <>{span.toFixed(2)} hours between start and end. </> : null}
                                Hourly pay counts from the start to the end of these hours; the lunch deduction stays the same. Being clocked in before the start or after the end is not paid, as with the company hours.
                            </div>

                            {history.length > 0 && (
                                <div className="mt-3">
                                    <div className="fw-semibold small mb-1">History</div>
                                    <Table size="sm" className="mb-0">
                                        <tbody>
                                            {history.map((h) => (
                                                <tr key={pick(h, "id", "Id")}>
                                                    <td>{fmtTime(pick(h, "startTime", "StartTime"))} – {fmtTime(pick(h, "endTime", "EndTime"))}</td>
                                                    <td>{fmtDate(pick(h, "effectiveFrom", "EffectiveFrom"))}{pick(h, "effectiveTo", "EffectiveTo") ? ` to ${fmtDate(pick(h, "effectiveTo", "EffectiveTo"))}` : " onwards"}</td>
                                                    <td className="text-muted">set by {pick(h, "setByName", "SetByName") || pick(h, "setBy", "SetBy")}{pick(h, "note", "Note") ? ` · ${pick(h, "note", "Note")}` : ""}</td>
                                                    <td>
                                                        {pick(h, "isCurrent", "IsCurrent") ? <Badge color="success" pill>now</Badge> : pick(h, "isUpcoming", "IsUpcoming") ? <Badge color="info" pill>coming up</Badge> : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="light" type="button" onClick={closeEditor} disabled={saving}>Cancel</Button>
                            <Button color="primary" type="submit" disabled={saving}>{saving ? <Spinner size="sm" /> : "Save hours"}</Button>
                        </ModalFooter>
                    </Form>
                </Modal>
            </Container>
        </div>
    );
};

export default TeamWorkHours;
